// Wording for the subscription disclosure step, built from product data.
// Dependency-free so it can be unit tested under node.
//
// Mirrors DisputePackage::Documents::SubscriptionDisclosureText on the server,
// which renders the same terms and statement onto every acknowledgment row.
// Keep the two in step and bump DISCLOSURE_VERSION together with
// CURRENT_VERSION there whenever the wording changes.

// Bump whenever the wording shown to the customer changes. Stored on every
// acknowledgment row so the dispute package can reproduce the exact copy.
export const DISCLOSURE_VERSION = '2026-10-02.1';

export const START_STEP_TITLE = 'When your first retainers ship';
export const REMINDER_STEP_TITLE = 'Before every charge';
export const REMINDER_STEP =
  'We email you 30 days, 7 days and 1 day ahead. Change or cancel anytime from your account.';
export const TIER_HELP =
  'Your plan becomes the recommended subscription unless you choose another one below.';
export const PRICING_NOTE =
  'Prices shown are current Retainer Club member pricing. Pricing may change before your subscription renews.';
export const ACCOUNT_NOTE =
  "You will be notified before you're charged. Login to your account for renewal date and current pricing options.";
export const LOWER_TIERS_LINK = 'Additional plans';
export const HIDE_LOWER_TIERS_LINK = 'Hide additional plans';

export function formatCents(cents, locale = 'en-US') {
  return ((Number(cents) || 0) / 100).toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
  });
}

// "per year" / "per month" / "every N months", for a price.
export function cadenceText(months) {
  const count = Number(months) || 0;
  if (count === 0 || count === 12) {
    return 'per year';
  }
  if (count === 1) {
    return 'per month';
  }
  return `every ${count} months`;
}

// "every year" / "every month" / "every N months", for a renewal.
export function everyMonths(months) {
  const count = Number(months) || 0;
  if (count === 0 || count === 12) {
    return 'every year';
  }
  if (count === 1) {
    return 'every month';
  }
  return `every ${count} months`;
}

export function monthsText(months) {
  const count = Number(months) || 0;
  return `${count} ${count === 1 ? 'month' : 'months'}`;
}

// Months from the first shipment to the first charge: a plan's start
// interval, else one renewal cadence (a subscription bought outright).
// Mirrors Disclosure#first_renewal_months on the server.
export function firstRenewalMonths({ planStartInterval, cadenceMonths }) {
  let months = Number(planStartInterval) || 0;
  if (months <= 0) {
    months = Number(cadenceMonths) || 0;
  }
  return months > 0 ? months : 12;
}

// The three "how it works" steps, as { title, body }.
export function steps({
  planName,
  subscription,
  tierName,
  cadenceMonths,
  firstRenewalMonths: firstMonths,
}) {
  const later = `${monthsText(firstMonths)} later`;
  const middle = subscription
    ? `Your first renewal, then ${everyMonths(cadenceMonths)} after that until you cancel.`
    : `Your plan becomes the ${tierName}, renewing ${everyMonths(cadenceMonths)} until you cancel.`;

  return [
    { title: START_STEP_TITLE, body: `Your ${planName} begins.` },
    { title: later, body: middle },
    { title: REMINDER_STEP_TITLE, body: REMINDER_STEP },
  ];
}

// The sentence beside the agreement checkbox. Names the tier and its current
// price so the ledger records what the customer agreed to pay.
export function acknowledgmentStatement({
  planName,
  subscription,
  tierName,
  priceCents,
  cadenceMonths,
  firstRenewalMonths: firstMonths,
  locale = 'en-US',
}) {
  const price = `currently ${formatCents(priceCents, locale)} ${cadenceText(cadenceMonths)}`;
  const starting = `starting ${monthsText(firstMonths)} after my first retainers ship`;

  if (subscription) {
    return `I agree that my ${planName}, ${price}, renews automatically ${starting}, and charges my card on file until I cancel from my account.`;
  }
  return `I agree that my ${planName} becomes the ${tierName}, ${price}, ${starting}, and that it renews automatically and charges my card on file until I cancel from my account.`;
}

// Price of one set: the renewal price split across the sets it delivers.
// null when the tier reports no sets.
export function perSetCents(priceCents, units) {
  const count = Number(units) || 0;
  if (count <= 0) {
    return null;
  }
  return Math.round((Number(priceCents) || 0) / count);
}

// "$239" for whole dollars, "$239.50" otherwise. Used on the tier card's
// total line, where a trailing ".00" is noise.
export function formatWholeCents(cents, locale = 'en-US') {
  const amount = Number(cents) || 0;
  return (amount / 100).toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

// "billed annually" / "billed monthly" / "billed every N months"
export function billedText(months) {
  const count = Number(months) || 0;
  if (count === 0 || count === 12) {
    return 'billed annually';
  }
  if (count === 1) {
    return 'billed monthly';
  }
  return `billed every ${count} months`;
}

// The line under a tier's per-set price: what is actually charged each
// renewal. "$239 total - current member price billed annually"
export function totalPriceLine({ priceCents, cadenceMonths, locale = 'en-US' }) {
  return `${formatWholeCents(priceCents, locale)} total - current member price ${billedText(cadenceMonths)}`;
}

// Which tiers a product shows up front and which sit behind the
// "Additional plans" link. A plan shows only the tier it becomes by default;
// every other tier, cheaper or dearer, is additional. A subscription bought
// outright shows just itself (there is no choice). When a plan's default is
// not in the list, every tier shows so the customer is never left with
// nothing. Mirrors Disclosure#mark_collapsed on the server, which records
// the same split on the ledger.
export function splitTiers({ product, tiers }) {
  const list = tiers || [];
  const subscription = product?.productType?.toLowerCase() === 'subscription';
  if (subscription) {
    return {
      upFront: list.filter((tier) => tier.product.id === product.id),
      additional: [],
    };
  }

  const defaultId = product?.planSubscription?.id;
  if (!list.some((tier) => tier.product.id === defaultId)) {
    return { upFront: list, additional: [] };
  }
  return {
    upFront: list.filter((tier) => tier.product.id === defaultId),
    additional: list.filter((tier) => tier.product.id !== defaultId),
  };
}
